import React, {
  createContext,
  type MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

/**
 * Saves the open plan's changes, then calls `proceed`; or, when they can't
 * be saved (a required field is empty, or the save failed), calls
 * `onCannotSave` with what to fix instead.
 */
export type PlanLeaveGuard = (
  proceed: () => void,
  onCannotSave: (reason: string) => void
) => void;

const PlanLeaveGuardContext =
  createContext<MutableRefObject<PlanLeaveGuard | null> | null>(null);

/**
 * Lets the farm tree ask Planning to save before it opens another plan, so
 * leaving a plan from the tree saves the changes rather than asking to
 * discard them — the same as leaving the page.
 */
export const PlanLeaveGuardProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const guardRef = useRef<PlanLeaveGuard | null>(null);
  return (
    <PlanLeaveGuardContext.Provider value={guardRef}>
      {children}
    </PlanLeaveGuardContext.Provider>
  );
};

/** Planning registers how it saves before a plan is left. */
export const useRegisterPlanLeaveGuard = (guard: PlanLeaveGuard) => {
  const guardRef = useContext(PlanLeaveGuardContext);
  useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = guard;
    return () => {
      if (guardRef.current === guard) guardRef.current = null;
    };
  }, [guardRef, guard]);
};

/**
 * Asks Planning to save, then `proceed`. Returns false when Planning isn't
 * open to ask, so the caller can fall back to its own handling.
 */
export const useLeavePlanAfterSaving = () => {
  const guardRef = useContext(PlanLeaveGuardContext);
  return useCallback(
    (proceed: () => void, onCannotSave: (reason: string) => void): boolean => {
      const guard = guardRef?.current;
      if (!guard) return false;
      guard(proceed, onCannotSave);
      return true;
    },
    [guardRef]
  );
};
